"use client";
import { BMPrimitives, BMText, useBMTheme } from "@traveloka/district-bloom";
import { useCallback, useMemo } from "react";
import { StyleSheet } from "react-native";

import Image from "../../Image";
import BMView from "../../View/BMView";

import AnalyticsBarChart, {
  ChartTooltipProps,
} from "../AnalyticsBarChart/AnalyticsBarChart";
import { ViewType } from "../types";
import { DEFAULT_CURRENCY_FORMATTER } from "./constants";
import {
  ProductBarChartEntry,
  ProductBarChartTransaction,
  ProductBarChartValueFormatter,
} from "./types";
import { rankIndexOf, toNumericValue } from "./utils";
import useProductBarChartSeries from "./useProductBarChartSeries";

type Props = {
  transactions: ProductBarChartTransaction[];
  note?: string;
  valueFormatter?: ProductBarChartValueFormatter;
  viewType?: ViewType;
  interactive?: boolean;
};

export default function ProductBarChart({
  transactions,
  note,
  valueFormatter = DEFAULT_CURRENCY_FORMATTER,
  viewType = "MONTHLY",
  interactive = true,
}: Props) {
  const {
    series,
    nameToColor,
    legendItems,
    chartData,
    sortedEntriesByDate,
    getCellFill,
  } = useProductBarChartSeries(transactions, viewType);

  const allValues = useMemo(
    () =>
      transactions.flatMap((t) => t.entries.map((e) => toNumericValue(e.value))),
    [transactions]
  );

  const { rawMax, rawMin } = useMemo(() => {
    const [maxValue, minValue] =
      allValues.length > 0
        ? [Math.max(...allValues), Math.min(...allValues)]
        : [0, 0];
    return { rawMax: maxValue * 1.05, rawMin: minValue * 0.5 };
  }, [allValues]);

  const { yMin, yMax } = useMemo(() => {
    // guard against log10(0) = -Infinity when all values are zero or no data
    if (rawMax <= 0) {
      return { yMin: 0, yMax: valueFormatter.emptyDomainMax };
    }

    const magMax = Math.pow(10, Math.floor(Math.log10(rawMax)) - 1);
    const magMin =
      rawMin > 0 ? Math.pow(10, Math.floor(Math.log10(rawMin)) - 1) : magMax;
    const yMax = Math.ceil(rawMax / magMax) * magMax;
    const yMin = rawMin > 0 ? Math.floor(rawMin / magMin) * magMin : 0;

    return { yMin, yMax };
  }, [rawMax, rawMin, valueFormatter]);

  const yAxisDomain = useMemo<[number, number]>(
    () => [yMin, yMax],
    [yMin, yMax]
  );

  const yAxisTicks = useMemo(
    () => Array.from({ length: 6 }, (_, i) => yMin + ((yMax - yMin) / 5) * i),
    [yMin, yMax]
  );

  const formatYAxisTick = useCallback(
    (v: string | number) => {
      if (v === yMin) return "";
      return valueFormatter.formatYAxisTick(Number(v));
    },
    [yMin, valueFormatter]
  );

  const formatXAxisTick = useCallback(
    (v: string | number) => {
      const str = String(v);
      if (viewType === "ANNUALLY") return str;

      const [period, year] = str.split(" ");
      if (viewType === "QUARTERLY") return `${period} '${year.slice(2)}`;
      return `${period.slice(0, 3)} '${year.slice(2)}`;
    },
    [viewType]
  );

  const renderTooltip = useCallback(
    ({ active, payload, label }: ChartTooltipProps) => {
      if (!active || !payload?.length) return null;
      return (
        <ProductBarChartTooltip
          label={label}
          payload={payload}
          sortedEntries={(label && sortedEntriesByDate[label]) || []}
          nameToColor={nameToColor}
          formatTooltipValue={valueFormatter.formatTooltipValue}
          viewType={viewType}
        />
      );
    },
    [sortedEntriesByDate, nameToColor, valueFormatter, viewType]
  );

  return (
    <AnalyticsBarChart
      data={chartData}
      series={series}
      legendItems={legendItems}
      getCellFill={getCellFill}
      yAxisWidth={25}
      yAxisTicks={yAxisTicks}
      yAxisDomain={yAxisDomain}
      formatYAxisTick={formatYAxisTick}
      formatXAxisTick={formatXAxisTick}
      renderTooltip={interactive ? renderTooltip : undefined}
      showLegend={!interactive}
      note={note}
    />
  );
}

type ProductBarChartTooltipProps = {
  label?: string;
  payload: NonNullable<ChartTooltipProps["payload"]>;
  sortedEntries: ProductBarChartEntry[];
  nameToColor: Record<string, string>;
  formatTooltipValue: ProductBarChartValueFormatter["formatTooltipValue"];
  viewType: ViewType;
};

function ProductBarChartTooltip({
  label,
  payload,
  sortedEntries,
  nameToColor,
  formatTooltipValue,
  viewType,
}: ProductBarChartTooltipProps) {
  const { BMComponents } = useBMTheme();
  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: {
          padding: BMPrimitives.BMSize.sm,
          borderRadius: BMPrimitives.BMRadius.md,
          backgroundColor: BMComponents.BMStyleSurfaceBgcolor,
          borderColor: BMComponents.BMStyleLineBordercolorSubtle,
          borderWidth: BMPrimitives.BMBorder.width.thin,
        },
        square: {
          width: BMPrimitives.BMSize.sm,
          height: BMPrimitives.BMSize.sm,
        },
      }),
    [BMComponents]
  );

  const { full, range } = useMemo(() => {
    if (!label) return { full: "", range: "" };

    if (viewType === "ANNUALLY" || viewType === "QUARTERLY") {
      return { full: label, range: "" };
    }

    const [month, year] = label.split(" ");
    if (!month || !year) return { full: "", range: "" };

    const daysInMonth = new Date(
      Number(year),
      new Date(`${month} 1`).getMonth() + 1,
      0
    ).getDate();

    return { full: label, range: `(1 - ${daysInMonth} ${month.slice(0, 3)})` };
  }, [label, viewType]);

  return (
    <BMView style={styles.container} spacing="md">
      {full || range ? (
        <BMView row align="center" spacing="xs">
          <BMText variant="uiSmall" ink="BMStyleTextInkSecondary">
            {full}
          </BMText>
          <BMText variant="uiSmallLi" ink="BMStyleTextInkSecondary">
            {range}
          </BMText>
        </BMView>
      ) : null}
      <BMView spacing="xs">
        {payload
          .filter((entry) => typeof entry.value === "number")
          .flatMap((entry) => {
            const product = sortedEntries[rankIndexOf(String(entry.dataKey))];
            return product ? [{ entry, product }] : [];
          })
          .map(({ entry, product }, i) => (
            <BMView key={entry.dataKey} row align="center" spacing="xs">
              <BMView
                style={[
                  styles.square,
                  { backgroundColor: nameToColor[product.name] },
                ]}
              />
              <BMText variant="paragraphSmall" ink="BMStyleTextInkSecondary">
                {i + 1}. {product.name}
              </BMText>
              {product.logoUrl ? (
                <Image src={product.logoUrl} width={14} height={14} />
              ) : null}
              <BMText variant="paragraphSmall" ink="BMStyleTextInkSecondary">
                :
              </BMText>
              <BMText variant="paragraphSmallEm">
                {formatTooltipValue(Number(entry.value), product.value)}
              </BMText>
            </BMView>
          ))}
      </BMView>
    </BMView>
  );
}
